
import { useState } from 'react';
import { useKnowledgeEntries } from '@/hooks/useKnowledgeEntries';
import { useKnowledgeSearch } from '@/hooks/useKnowledgeSearch';
import { KnowledgeEntry } from '@/components/knowledge/types';
import { useToast } from '@/hooks/use-toast';
import { supabase } from "@/integrations/supabase/client";

export function useKnowledgeBase() {
  const { entries, isLoading, invalidateEntries } = useKnowledgeEntries();
  const { isSearching, handleSearch } = useKnowledgeSearch();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const handleDelete = async (entry: KnowledgeEntry): Promise<void> => {
    if (isDeleting) return;

    try {
      setIsDeleting(true);
      console.log(`Deleting entry with ID: ${entry.id}`);
      console.log('Supabase client configuration:', {
        clientIsConfigured: !!supabase,
        // Log authentication status instead of internal properties
        authSession: await supabase.auth.getSession()
      });
      
      // Log before making the edge function call
      console.log('About to call knowledge-management edge function with method DELETE');
      
      // Delete entry using the edge function to ensure all OpenAI resources are cleaned up
      const { data, error } = await supabase.functions.invoke('knowledge-management', {
        method: 'DELETE',
        body: { 
          id: entry.id,
          operation: 'delete_entry'
        }
      });

      console.log('Edge function response:', { data, error });

      if (error) {
        console.error('Delete error:', error);
        throw new Error(`Failed to delete entry: ${error.message}`);
      }
      
      if (!data?.success) {
        console.error('Deletion was not successful:', data);
        throw new Error(data?.error || 'Unknown error during deletion');
      }

      // Invalidate and refetch entries
      console.log('Delete successful, invalidating entries cache');
      await invalidateEntries();

      toast({
        title: "Entry Deleted",
        description: data?.message || "Knowledge entry has been deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting entry:', error);
      
      // Try a direct database deletion as fallback
      console.log('Attempting direct database deletion as fallback');
      try {
        const { error: dbError } = await supabase
          .from('knowledge_entries')
          .delete()
          .eq('id', entry.id);
          
        if (dbError) {
          console.error('Direct database deletion failed:', dbError);
          throw new Error(`Direct deletion failed: ${dbError.message}`);
        } else {
          console.log('Direct database deletion succeeded');
          await invalidateEntries();
          toast({
            title: "Entry Deleted",
            description: "Knowledge entry has been deleted using direct database access",
          });
          // Function succeeded through the fallback, so don't throw
          return;
        }
      } catch (fallbackError) {
        console.error('Fallback deletion failed:', fallbackError);
      }
      
      // If we get here, both attempts failed
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error 
          ? `Failed to delete knowledge entry: ${error.message}` 
          : "Failed to delete knowledge entry",
      });
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  const generateEmbeddingsForEntry = async (entry: KnowledgeEntry): Promise<void> => {
    if (isProcessing || !entry.content) return;
    
    try {
      setIsProcessing(true);
      
      toast({
        title: "Processing",
        description: "Generating embeddings for the entry...",
      });
      
      // Use the edge function for updating
      const { data, error } = await supabase.functions.invoke('knowledge-management', {
        method: 'PUT',
        body: { 
          id: entry.id,
          content: entry.content,
          operation: 'generate_embeddings'
        }
      });
      
      if (error) {
        console.error('Embedding generation error:', error);
        throw new Error(`Failed to generate embeddings: ${error.message || error}`);
      }
      
      if (!data?.success) {
        console.error('Embedding generation was not successful:', data);
        throw new Error(data?.error || 'Unknown error during embedding generation');
      }
      
      // Invalidate and refetch entries
      await invalidateEntries();
      
      toast({
        title: "Processing Completed",
        description: "The entry has been processed successfully",
      });
    } catch (error) {
      console.error('Error generating embeddings:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error 
          ? `Failed to process entry: ${error.message}` 
          : "Failed to process entry",
      });
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    entries,
    isLoading,
    isDeleting,
    isProcessing,
    isSearching,
    handleSearch,
    handleDelete,
    generateEmbeddingsForEntry,
    invalidateEntries
  };
}
